import React from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from 'lib/utils';
import { ExamBar } from './ExamBar';
import { ExamClockChip, type ExamClock } from './ExamTimer';

export interface ExamPhoneHeaderProps {
    /** The test title: the page heading, visually hidden on phones. */
    title: string;
    /** 1-based index of the current question. */
    questionNumber: number;
    totalQuestions: number;
    sectionTitle: string | null;
    /** Autosave or preview state shown after the section ("Saqlangan"); `null` hides it. */
    statusText: string | null;
    /** `null` when the test is untimed or the start page hides the timer. */
    clock: ExamClock | null;
    showProgressBar: boolean;
    progressPct: number;
    className?: string;
}

/** 60px sticky phone header: position, section and save state, the timer, and a 3px ink progress line. */
export function ExamPhoneHeader({
    title,
    questionNumber,
    totalQuestions,
    sectionTitle,
    statusText,
    clock,
    showProgressBar,
    progressPct,
    className,
}: ExamPhoneHeaderProps) {
    const { t } = useTranslation();
    return (
        <header
            className={cn(
                'sticky top-0 z-20 flex h-[60px] shrink-0 items-center border-b border-border bg-card px-4',
                className,
            )}
        >
            <h1 className="sr-only">{title}</h1>
            {/* On tablets the content lines up with the 720px question column. */}
            <div className="mx-auto flex w-full max-w-[688px] items-center justify-between gap-3">
                <div className="flex min-w-0 flex-col gap-0.5 leading-tight">
                    <span className="text-[15px] font-bold">
                        {t('question')}{' '}
                        <span className="tabular-nums">
                            {questionNumber} {t('of')} {totalQuestions}
                        </span>
                    </span>
                    {(sectionTitle || statusText) && (
                        <span className="truncate text-[12.5px] text-muted-foreground">
                            {sectionTitle}
                            {sectionTitle && statusText && ' · '}
                            {statusText && <span role="status">{statusText}</span>}
                        </span>
                    )}
                </div>
                {clock && <ExamClockChip clock={clock} size="sm" showPerQuestionLabel={false} />}
            </div>
            {showProgressBar && (
                <ExamBar pct={progressPct} className="absolute inset-x-0 -bottom-px h-[3px] rounded-none bg-border" />
            )}
        </header>
    );
}

export default ExamPhoneHeader;
